"use client";

import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Palette } from "lucide-react";
import { HexColorPicker } from "react-colorful";

import type {
  IBrandQuoteThemeColorPalette,
  QuoteColorSchema,
} from "@/@sdk/brands/brand-model";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  EXTENDED_QUOTE_COLOR_PALETTE_PRESETS,
  FEATURED_QUOTE_COLOR_PALETTE_PRESETS,
  type QuoteColorPalettePreset,
} from "@/components/quotes/quotes-template/quote-theme/quote-theme-presets";

function normalizeHexColor(value: string): string {
  if (!value) return value;
  return value.startsWith("#") ? value : `#${value}`;
}

function isValidHexColor(value: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
}

function ColorSwatchPicker({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const displayColor = isValidHexColor(value) ? value : "#000000";

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              className="h-10 w-10 shrink-0 rounded-md p-0"
              style={{ backgroundColor: displayColor }}
            >
              <span className="sr-only">Pick {label.toLowerCase()}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <HexColorPicker color={displayColor} onChange={onChange} />
          </PopoverContent>
        </Popover>
        <Input
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "" || isValidHexColor(normalizeHexColor(next))) {
              onChange(next === "" ? next : normalizeHexColor(next));
            }
          }}
          placeholder="#F15933"
          className="font-mono uppercase"
        />
      </div>
    </div>
  );
}

function PaletteTile({
  preset,
  colorSchema,
  brandPrimaryColor,
  isSelected,
  onSelect,
}: {
  preset: QuoteColorPalettePreset;
  colorSchema: QuoteColorSchema;
  brandPrimaryColor?: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const primary =
    preset.presetId === "brand-primary" && brandPrimaryColor
      ? brandPrimaryColor
      : preset.primary;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
        isSelected && "border-primary ring-1 ring-primary"
      )}
    >
      <div className="mb-2 flex gap-1">
        <span
          className="h-6 w-6 rounded-full border"
          style={{ backgroundColor: primary }}
        />
        {colorSchema === "DUAL_TONE" && preset.secondary ? (
          <span
            className="h-6 w-6 rounded-full border"
            style={{ backgroundColor: preset.secondary }}
          />
        ) : null}
      </div>
      <p className="text-xs font-medium leading-tight">{preset.name}</p>
    </button>
  );
}

export function ColorPalettePicker({
  colorSchema,
  value,
  brandPrimaryColor,
  onChange,
}: {
  colorSchema: QuoteColorSchema;
  value: IBrandQuoteThemeColorPalette;
  brandPrimaryColor?: string;
  onChange: (value: IBrandQuoteThemeColorPalette) => void;
}) {
  const resolvedPrimary =
    value.presetId === "brand-primary" && brandPrimaryColor
      ? brandPrimaryColor
      : value.primary;

  const isExtendedSelection = EXTENDED_QUOTE_COLOR_PALETTE_PRESETS.some(
    (preset) => preset.presetId === value.presetId
  );
  const [showExtended, setShowExtended] = useState(isExtendedSelection);

  useEffect(() => {
    if (isExtendedSelection) {
      setShowExtended(true);
    }
  }, [isExtendedSelection]);

  const isCustomMode =
    value.presetId === "custom" || value.presetId === "brand-primary";

  const visiblePresets = showExtended
    ? [...FEATURED_QUOTE_COLOR_PALETTE_PRESETS, ...EXTENDED_QUOTE_COLOR_PALETTE_PRESETS]
    : FEATURED_QUOTE_COLOR_PALETTE_PRESETS;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {visiblePresets.map((preset) => {
          const primary =
            preset.presetId === "brand-primary" && brandPrimaryColor
              ? brandPrimaryColor
              : preset.primary;

          return (
            <PaletteTile
              key={preset.id}
              preset={preset}
              colorSchema={colorSchema}
              brandPrimaryColor={brandPrimaryColor}
              isSelected={value.presetId === preset.presetId}
              onSelect={() =>
                onChange({
                  presetId: preset.presetId,
                  primary,
                  secondary: preset.secondary,
                })
              }
            />
          );
        })}

        <button
          type="button"
          onClick={() =>
            onChange({
              presetId: "custom",
              primary: resolvedPrimary,
              secondary: value.secondary,
            })
          }
          className={cn(
            "rounded-lg border border-dashed p-3 text-left transition-colors hover:bg-muted/50",
            value.presetId === "custom" && "border-primary ring-1 ring-primary"
          )}
        >
          <div className="mb-2 flex h-6 items-center">
            <Palette className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-xs font-medium leading-tight">Custom colours</p>
        </button>
      </div>

      {EXTENDED_QUOTE_COLOR_PALETTE_PRESETS.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => setShowExtended((current) => !current)}
        >
          {showExtended ? (
            <ChevronUp className="mr-1.5 h-4 w-4" />
          ) : (
            <ChevronDown className="mr-1.5 h-4 w-4" />
          )}
          {showExtended ? "Show fewer palettes" : "Show more palettes"}
        </Button>
      ) : null}

      {isCustomMode ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <ColorSwatchPicker
            id="custom-primary"
            label="Primary colour"
            value={resolvedPrimary}
            disabled={value.presetId === "brand-primary"}
            onChange={(primary) =>
              onChange({
                ...value,
                presetId: "custom",
                primary,
              })
            }
          />
          {colorSchema === "DUAL_TONE" ? (
            <ColorSwatchPicker
              id="custom-secondary"
              label="Secondary colour"
              value={value.secondary ?? "#ffffff"}
              onChange={(secondary) =>
                onChange({
                  ...value,
                  presetId:
                    value.presetId === "brand-primary" ? "custom" : value.presetId,
                  secondary,
                })
              }
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
